import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, Money, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Employees() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [openEmp, setOpenEmp] = useState(false);
  const [openPay, setOpenPay] = useState(false);
  const [selected, setSelected] = useState(null);
  const [empForm, setEmpForm] = useState({
    name: "", position: "", monthly_salary: "", currency: "TRY", hourly_overtime_rate: "", start_date: "", phone: "",
  });
  const [payForm, setPayForm] = useState({
    kind: "salary", amount: "", currency: "TRY", date: new Date().toISOString().slice(0, 10),
    period_month: new Date().getMonth() + 1, period_year: new Date().getFullYear(),
    bank_account_id: "", note: "",
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data,
  });
  const { data: banks = [] } = useQuery({
    queryKey: ["banks"], queryFn: async () => (await api.get("/bank-accounts")).data,
  });
  const { data: payroll = [] } = useQuery({
    queryKey: ["payroll", selected?.id],
    queryFn: async () => (await api.get(selected ? `/payroll?employee_id=${selected.id}` : "/payroll")).data,
    enabled: !!selected,
  });

  const [editingEmp, setEditingEmp] = useState(null);
  const resetEmpForm = () => setEmpForm({ name: "", position: "", monthly_salary: "", currency: "TRY", hourly_overtime_rate: "", start_date: "", phone: "" });

  const createEmp = useMutation({
    mutationFn: async (p) => (await api.post("/employees", p)).data,
    onSuccess: () => {
      qc.invalidateQueries(); setOpenEmp(false); setEditingEmp(null); resetEmpForm();
      toast.success("Çalışan eklendi");
    },
  });
  const updateEmp = useMutation({
    mutationFn: async ({ id, p }) => (await api.put(`/employees/${id}`, p)).data,
    onSuccess: (data) => {
      qc.invalidateQueries(); setOpenEmp(false); setEditingEmp(null); resetEmpForm();
      if (selected && data?.id === selected.id) setSelected(data);
      toast.success("Çalışan güncellendi");
    },
  });
  const delEmp = useMutation({
    mutationFn: async (id) => (await api.delete(`/employees/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries(); setSelected(null); },
  });

  const startEditEmp = (e) => {
    setEditingEmp(e);
    setEmpForm({
      name: e.name || "", position: e.position || "",
      monthly_salary: String(e.monthly_salary ?? ""),
      currency: e.currency || "TRY",
      hourly_overtime_rate: String(e.hourly_overtime_rate ?? ""),
      start_date: e.start_date || "", phone: e.phone || "",
    });
    setOpenEmp(true);
  };
  const startCreateEmp = () => { setEditingEmp(null); resetEmpForm(); setOpenEmp(true); };
  const createPay = useMutation({
    mutationFn: async (p) => (await api.post("/payroll", p)).data,
    onSuccess: () => {
      qc.invalidateQueries(); setOpenPay(false); toast.success("Ödeme kaydedildi");
    },
  });
  const delPay = useMutation({
    mutationFn: async (id) => (await api.delete(`/payroll/${id}`)).data,
    onSuccess: () => qc.invalidateQueries(),
  });

  const submitEmp = (e) => {
    e.preventDefault();
    const payload = {
      ...empForm,
      monthly_salary: parseFloat(empForm.monthly_salary || 0),
      hourly_overtime_rate: parseFloat(empForm.hourly_overtime_rate || 0),
    };
    if (editingEmp) updateEmp.mutate({ id: editingEmp.id, p: payload });
    else createEmp.mutate(payload);
  };
  const submitPay = (e) => {
    e.preventDefault();
    createPay.mutate({
      ...payForm,
      employee_id: selected.id,
      amount: parseFloat(payForm.amount),
      period_month: parseInt(payForm.period_month) || null,
      period_year: parseInt(payForm.period_year) || null,
      bank_account_id: payForm.bank_account_id || null,
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="employees-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Personel & Maaş</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Çalışanlar</h1>
        </div>
        {isAdmin && (
          <Button data-testid="emp-add-btn" onClick={startCreateEmp}>
            <Plus size={16} className="mr-1" /> Yeni Çalışan
          </Button>
        )}
        <Dialog open={openEmp} onOpenChange={(v) => { setOpenEmp(v); if (!v) setEditingEmp(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingEmp ? "Çalışan Düzenle" : "Yeni Çalışan"}</DialogTitle></DialogHeader>
            <form onSubmit={submitEmp} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ad Soyad</Label><Input required value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} data-testid="emp-name" /></div>
                <div><Label>Pozisyon</Label><Input value={empForm.position} onChange={(e) => setEmpForm({ ...empForm, position: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Aylık Maaş</Label><Input type="number" step="0.01" value={empForm.monthly_salary} onChange={(e) => setEmpForm({ ...empForm, monthly_salary: e.target.value })} /></div>
                <div>
                  <Label>Para Birimi</Label>
                  <Select value={empForm.currency} onValueChange={(v) => setEmpForm({ ...empForm, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Saatlik Mesai Ücreti</Label><Input type="number" step="0.01" value={empForm.hourly_overtime_rate} onChange={(e) => setEmpForm({ ...empForm, hourly_overtime_rate: e.target.value })} /></div>
                <div><Label>İşe Başlama</Label><Input type="date" value={empForm.start_date} onChange={(e) => setEmpForm({ ...empForm, start_date: e.target.value })} /></div>
              </div>
              <div><Label>Telefon</Label><Input value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} /></div>
              <DialogFooter><Button type="submit" data-testid="emp-save">{editingEmp ? "Güncelle" : "Kaydet"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-md border border-border bg-white lg:col-span-1">
          {employees.map((e) => (
            <div key={e.id}
              className={`flex w-full items-center justify-between border-b border-border/60 px-2 py-2 text-sm hover:bg-secondary ${selected?.id === e.id ? "bg-secondary" : ""}`}>
              <button
                onClick={() => setSelected(e)}
                data-testid={`emp-row-${e.id}`}
                className="flex flex-1 items-center justify-between px-2 py-1 text-left"
              >
                <div>
                  <div className="font-medium">{e.name}</div>
                  <div className="text-xs text-muted-foreground">{e.position || "-"}</div>
                </div>
                <div className="mono text-xs mr-3 text-right" data-testid={`emp-year-paid-${e.id}`}>
                  <div className="text-[10px] uppercase text-muted-foreground">Toplam Alınan</div>
                  {fmtMoney(e.year_paid_try || 0, "TRY")}
                </div>
              </button>
              {isAdmin && <button
                onClick={(ev) => { ev.stopPropagation(); startEditEmp(e); }}
                data-testid={`edit-emp-${e.id}`}
                className="rounded border border-border p-1.5 text-foreground hover:bg-white"
                title="Düzenle"
              >
                <PencilSimple size={14} />
              </button>}
            </div>
          ))}
          {employees.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">Henüz çalışan yok.</div>
          )}
        </div>

        <div className="rounded-md border border-border bg-white lg:col-span-2">
          {!selected && (
            <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-muted-foreground">
              Maaş/avans ödemelerini görmek için bir çalışan seçin.
            </div>
          )}
          {selected && (
            <div className="p-6" data-testid="emp-detail">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">Çalışan</div>
                  <div className="mt-1 text-2xl font-semibold">{selected.name}</div>
                  <div className="text-sm text-muted-foreground">{selected.position}</div>
                  <div className="mono mt-2 text-sm">
                    Aylık Maaş: {fmtMoney(selected.monthly_salary, selected.currency)} · Mesai Saat: {fmtMoney(selected.hourly_overtime_rate, selected.currency)}
                  </div>
                </div>
                {isAdmin && <div className="flex gap-2">
                  <Dialog open={openPay} onOpenChange={setOpenPay}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="pay-add-btn"><Money size={14} className="mr-1" /> Ödeme Ekle</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Yeni Ödeme — {selected.name}</DialogTitle></DialogHeader>
                      <form onSubmit={submitPay} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Tür</Label>
                            <Select value={payForm.kind} onValueChange={(v) => setPayForm({ ...payForm, kind: v })}>
                              <SelectTrigger data-testid="pay-kind"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="salary">Maaş</SelectItem>
                                <SelectItem value="advance">Avans</SelectItem>
                                <SelectItem value="cc_expense">KK Harcama</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Tutar</Label>
                            <Input required type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label>Para Birimi</Label>
                            <Select value={payForm.currency} onValueChange={(v) => setPayForm({ ...payForm, currency: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
                            </Select>
                          </div>
                          <div><Label>Tarih</Label><Input type="date" required value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
                          <div>
                            <Label>Banka</Label>
                            <Select value={payForm.bank_account_id || "__none__"} onValueChange={(v) => setPayForm({ ...payForm, bank_account_id: v === "__none__" ? "" : v })}>
                              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— Yok —</SelectItem>
                                {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>Dönem Ay (1-12)</Label><Input type="number" value={payForm.period_month} onChange={(e) => setPayForm({ ...payForm, period_month: e.target.value })} /></div>
                          <div><Label>Dönem Yıl</Label><Input type="number" value={payForm.period_year} onChange={(e) => setPayForm({ ...payForm, period_year: e.target.value })} /></div>
                        </div>
                        <div><Label>Not</Label><Textarea value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} /></div>
                        <DialogFooter><Button type="submit" data-testid="pay-save">Kaydet</Button></DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <button onClick={() => delEmp.mutate(selected.id)} data-testid={`del-emp-${selected.id}`} className="rounded border border-border p-2 text-destructive hover:bg-destructive/5">
                    <Trash size={14} />
                  </button>
                </div>}
              </div>

              <div className="mt-6">
                <div className="overline mb-2">Maaş & Avans Geçmişi</div>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead className="border-b border-border">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2">Tarih</th><th>Tür</th><th>Dönem</th><th className="text-right">Tutar</th><th className="text-right">TL</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payroll.map((p) => (
                      <tr key={p.id} className="border-b border-border/60">
                        <td className="py-2 mono">{fmtDate(p.date)}</td>
                        <td>{p.kind === "salary" ? "Maaş" : p.kind === "cc_expense" ? "KK Harcama" : "Avans"}</td>
                        <td className="mono">{p.period_month ? `${p.period_year}-${String(p.period_month).padStart(2, "0")}` : "-"}</td>
                        <td className="text-right mono">{fmtMoney(p.amount, p.currency)}</td>
                        <td className="text-right mono text-muted-foreground">{fmtMoney(p.amount_try, "TRY")}</td>
                        <td className="text-right">
                          {isAdmin && <button onClick={() => delPay.mutate(p.id)} data-testid={`del-pay-${p.id}`} className="text-destructive hover:opacity-70"><Trash size={12} /></button>}
                        </td>
                      </tr>
                    ))}
                    {payroll.length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Kayıt yok.</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
